import { useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SEARCH_CONFIG } from "config/searchConfig"; // keep your existing path

// ---- helpers ----
const toBool = (v) => v === "1" || v === "true";
const parseNum = (v, fallback) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};
const joinValues = (arr) =>
  (arr || [])
    .map((o) => (typeof o === "string" ? o : o?.value))
    .filter(Boolean)
    .join(",");

const pickOptions = (csv, allOptions) => {
  if (!csv) return [];
  const set = new Set(csv.split(",").filter(Boolean));
  return (allOptions || []).filter((o) => set.has(o.value));
};

function decode({ type, raw, def, options }) {
  switch (type) {
    case "boolean":
      return raw != null ? toBool(raw) : def;
    case "number":
      return raw != null ? parseNum(raw, def) : def;
    case "multi":
      return pickOptions(raw, options);
    default:
      return raw ?? def; // strings/dates as strings
  }
}

function encode({ type, val }) {
  if (val == null || val === "") return "";
  if (type === "boolean") return val ? "1" : "0";
  if (type === "multi") return joinValues(val);
  return String(val);
}

// ---- main hook ----
export function usePersistedSearch({
  namespace = "", // still used to pick the schema only
  campaignOptions = [],
  gatewayOptions = [],
}) {
  const [params, setParams] = useSearchParams();
  const cfg = SEARCH_CONFIG[namespace];
  if (!cfg || !Array.isArray(cfg.fields)) {
    throw new Error(`usePersistedSearch: unknown namespace "${namespace}"`);
  }

  const optionsByProp = useMemo(
    () => ({ campaignOptions, gatewayOptions }),
    [campaignOptions, gatewayOptions]
  );

  // Read from URL (UNPREFIXED KEYS)
  const filters = useMemo(() => {
    const out = {};
    for (const f of cfg.fields) {
      const raw = params.get(f.key);
      const opts =
        f.type === "multi" ? optionsByProp[f.optionsProp] : undefined;
      out[f.key] = decode({ type: f.type, raw, def: f.default, options: opts });
    }
    return out;
  }, [params, cfg.fields, optionsByProp]);

  // Write to URL (UNPREFIXED KEYS)
  const write = useCallback(
    (next, { push = false } = {}) => {
      const p = new URLSearchParams(params);
      for (const f of cfg.fields) {
        const key = f.key;
        const enc = encode({ type: f.type, val: next[f.key] });
        const encDef = encode({ type: f.type, val: f.default });

        if (!enc || enc === encDef) p.delete(key);
        else p.set(key, enc);
      }
      setParams(p, { replace: !push });
    },
    [params, setParams, cfg.fields]
  );

  // Generic setter
  const set = useCallback(
    (key, value, opts) => write({ ...filters, [key]: value }, opts),
    [filters, write]
  );

  // Per-field setters
  const setters = useMemo(() => {
    const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
    return Object.fromEntries(
      cfg.fields.map((f) => [`set${cap(f.key)}`, (v, o) => set(f.key, v, o)])
    );
  }, [cfg.fields, set]);

  const reset = useCallback(
    (opts) => {
      const next = cfg.fields.reduce(
        (acc, f) => ((acc[f.key] = f.default), acc),
        {}
      );
      write(next, opts);
    },
    [cfg.fields, write]
  );

  const setAll = useCallback(
    (patch = {}, opts) => write({ ...filters, ...patch }, opts),
    [filters, write]
  );

  return {
    filters,
    set,
    setAll,
    reset,
    ...setters,
    campaignOptions,
    gatewayOptions,
  };
}
